import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Stack,
    TextField,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridLoadingOverlay,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Organization, Attribute, AttributeCategory, AttributeValueType } from '../../utils';
import getAttributesForOrganization from '../../api/Attributes/getAttributesForOrganization';
import createAttributeForOrganization from '../../api/Attributes/createAttributeForOrganization';

interface AttributesPageProps {
    organization: Organization;
}

interface AttributeFormData {
    name: string;
    type: AttributeValueType;
    category: AttributeCategory;
    subCategory: string;
    choices: string[];
}

interface GetAttributesParams {
    name?: string;
    type?: string;
    category?: string;
    sub_category?: string;
    sort?:
        | 'name'
        | 'type'
        | 'category'
        | 'sub_category'
        | '-name'
        | '-type'
        | '-category'
        | '-sub_category';
    page?: number;
    per_page?: number;
}

function CustomLoadingOverlay() {
    return (
        <GridLoadingOverlay
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}
        >
            <CircularProgress />
        </GridLoadingOverlay>
    );
}

const AttributesPage: React.FC<AttributesPageProps> = ({ organization }) => {
    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackMessage, setSnackMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [newChoiceInput, setNewChoiceInput] = useState('');
    const [totalRows, setTotalRows] = useState<number>(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });

    // Separate search parameters from applied search parameters
    const [searchParams, setSearchParams] = useState<GetAttributesParams>({
        name: '',
        type: '',
        category: '',
        sub_category: '',
        sort: '-name',
    });

    // These are the parameters actually used for API calls
    const [appliedSearchParams, setAppliedSearchParams] = useState<GetAttributesParams>({
        name: '',
        type: '',
        category: '',
        sub_category: '',
        sort: '-name',
    });

    // Form data for creating new attribute
    const [formData, setFormData] = useState<AttributeFormData>({
        name: '',
        type: AttributeValueType.TEXT,
        category: AttributeCategory.Other,
        subCategory: '',
        choices: [],
    });

    const convertEnumToTitleCase = (str: string): string => {
        return str
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 150,
            sortable: true,
        },
        {
            field: 'type',
            headerName: 'Type',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => convertEnumToTitleCase(params.value),
        },
        {
            field: 'category',
            headerName: 'Category',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => convertEnumToTitleCase(params.value),
        },
        {
            field: 'sub_category',
            headerName: 'Sub Category',
            flex: 1,
            minWidth: 150,
            sortable: true,
        },
        {
            field: 'choices',
            headerName: 'Choices',
            flex: 2,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                if (
                    params.row.type === AttributeValueType.LIST &&
                    params.value &&
                    params.value.length > 0
                ) {
                    const choicesText = params.value.join(', ');
                    const tooltipContent = (
                        <Box>
                            {params.value.map((choice: string, index: number) => (
                                <Typography key={index} variant="body2" component="div">
                                    • {choice}
                                </Typography>
                            ))}
                        </Box>
                    );

                    return (
                        <Tooltip
                            title={tooltipContent}
                            arrow
                            placement="top-start"
                            sx={{
                                '& .MuiTooltip-tooltip': {
                                    maxWidth: 300,
                                    backgroundColor: 'rgba(97, 97, 97, 0.95)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'help',
                                    width: '100%',
                                }}
                            >
                                {choicesText}
                            </Box>
                        </Tooltip>
                    );
                }
                return params.row.type === AttributeValueType.LIST ? 'No choices defined' : 'N/A';
            },
        },
    ];

    const fetchAttributes = useCallback(
        (params?: GetAttributesParams) => {
            setLoading(true);
            const requestParams = {
                ...appliedSearchParams,
                ...params,
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
            };

            getAttributesForOrganization(organization.uuid, requestParams)
                .then((response) => {
                    setAttributes(response.data.data || []);
                    setTotalRows(response.data.meta?.total || 0);
                })
                .catch((error) => {
                    console.error('Error fetching attributes:', error);
                    setSnackMessage('Failed to fetch attributes');
                    setAttributes([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [organization.uuid, appliedSearchParams, paginationModel],
    );

    // Initial load only
    useEffect(() => {
        fetchAttributes();
    }, [fetchAttributes]);

    const handleSearchFieldChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setSearchParams({
            ...searchParams,
            [event.target.name as string]: event.target.value,
        });
    };

    const handleSelectChange = (field: string, value: string) => {
        setSearchParams({
            ...searchParams,
            [field]: value,
        });
    };

    const handleSearchClick = () => {
        setAppliedSearchParams(searchParams);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize }); // Reset to first page
    };

    const handleSort = (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        if (sortField && sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }
        const updatedSearchParams = {
            ...appliedSearchParams,
            sort: sortField as GetAttributesParams['sort'],
        };
        setAppliedSearchParams(updatedSearchParams);
    };

    const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
        setPaginationModel(newPaginationModel);
    };

    const handleCreateAttributeClick = () => {
        if (!formData.name || !formData.type || !formData.category) {
            setSnackMessage('Please fill in all required fields');
            return;
        }

        if (formData.type === AttributeValueType.LIST && formData.choices.length === 0) {
            setSnackMessage('List type attributes must have at least one choice');
            return;
        }

        setIsConfirmDialogOpen(true);
    };

    const handleCreateAttribute = async () => {
        try {
            await createAttributeForOrganization(
                organization.uuid,
                formData.name,
                formData.type,
                formData.category,
                formData.subCategory,
                formData.type === AttributeValueType.LIST ? formData.choices : undefined,
            );

            setSnackMessage('Attribute created successfully');
            setIsModalOpen(false);
            setIsConfirmDialogOpen(false);
            resetForm();
            fetchAttributes(); // Refresh the grid
        } catch (error: unknown) {
            const err = error as any;
            if (err?.response?.data?.message) {
                setSnackMessage(`Error: ${err.response.data.message}`);
                setIsConfirmDialogOpen(false);
                return;
            }
            console.error('Error creating attribute:', error);
            setSnackMessage('Failed to create attribute');
            setIsConfirmDialogOpen(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            type: AttributeValueType.TEXT,
            category: AttributeCategory.Other,
            subCategory: '',
            choices: [],
        });
        setNewChoiceInput('');
    };

    const addChoice = () => {
        if (newChoiceInput.trim() && !formData.choices.includes(newChoiceInput.trim())) {
            setFormData((prev) => ({
                ...prev,
                choices: [...prev.choices, newChoiceInput.trim()],
            }));
            setNewChoiceInput('');
        }
    };

    const removeChoice = (choiceToRemove: string) => {
        setFormData((prev) => ({
            ...prev,
            choices: prev.choices.filter((choice) => choice !== choiceToRemove),
        }));
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addChoice();
        }
    };

    return (
        <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
            <Snackbar
                open={!!snackMessage}
                autoHideDuration={6000}
                message={snackMessage}
                onClose={() => setSnackMessage('')}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h5">Attributes</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setIsModalOpen(true)}
                >
                    Create Attribute
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ marginY: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ marginRight: 1, display: 'flex', flexWrap: 'wrap' }}>
                    <TextField
                        name="name"
                        value={searchParams.name}
                        onChange={handleSearchFieldChange}
                        placeholder="Enter Name"
                        label="Name"
                        size="small"
                        style={{ margin: '2px' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160, margin: '2px' }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            name="type"
                            value={searchParams.type === '' ? 'all' : searchParams.type}
                            label="Type"
                            onChange={(e: SelectChangeEvent) => {
                                const value = e.target.value === 'all' ? '' : e.target.value;
                                handleSelectChange('type', value);
                            }}
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            {Object.values(AttributeValueType).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {convertEnumToTitleCase(type)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160, margin: '2px' }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            name="category"
                            value={searchParams.category === '' ? 'all' : searchParams.category}
                            label="Category"
                            onChange={(e: SelectChangeEvent) => {
                                const value = e.target.value === 'all' ? '' : e.target.value;
                                handleSelectChange('category', value);
                            }}
                        >
                            <MenuItem value="all">All Categories</MenuItem>
                            {Object.values(AttributeCategory).map((category) => (
                                <MenuItem key={category} value={category}>
                                    {convertEnumToTitleCase(category)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        name="sub_category"
                        value={searchParams.sub_category}
                        onChange={handleSearchFieldChange}
                        placeholder="Enter Sub Category"
                        label="Sub Category"
                        size="small"
                        style={{ margin: '2px' }}
                    />
                </Box>
                <Box>
                    <Button
                        size="medium"
                        onClick={handleSearchClick}
                        variant="contained"
                        sx={{ marginBottom: '8px' }}
                    >
                        Search Attributes
                    </Button>
                </Box>
            </Box>

            <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                    rows={attributes}
                    columns={columns}
                    loading={loading}
                    sortingMode="server"
                    onSortModelChange={handleSort}
                    pagination
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.uuid!}
                    slots={{
                        loadingOverlay: CustomLoadingOverlay,
                    }}
                />
            </Box>

            {/* Create Attribute Modal */}
            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Attribute</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Name *"
                            variant="outlined"
                            fullWidth
                            value={formData.name}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            style={{ marginTop: '8px', marginBottom: '8px' }}
                            size="small"
                        />

                        <FormControl fullWidth>
                            <InputLabel>Type *</InputLabel>
                            <Select
                                value={formData.type}
                                label="Type *"
                                onChange={(e: SelectChangeEvent) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        type: e.target.value as AttributeValueType,
                                        choices:
                                            e.target.value === AttributeValueType.LIST
                                                ? prev.choices
                                                : [],
                                    }))
                                }
                                style={{ marginBottom: '8px' }}
                                size="small"
                            >
                                {Object.values(AttributeValueType).map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {convertEnumToTitleCase(type)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Category *</InputLabel>
                            <Select
                                value={formData.category}
                                label="Category *"
                                onChange={(e: SelectChangeEvent) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        category: e.target.value as AttributeCategory,
                                    }))
                                }
                                style={{ marginBottom: '8px' }}
                                size="small"
                            >
                                {Object.values(AttributeCategory).map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {convertEnumToTitleCase(category)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Sub Category"
                            variant="outlined"
                            fullWidth
                            value={formData.subCategory}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, subCategory: e.target.value }))
                            }
                            style={{ marginBottom: '8px' }}
                            size="small"
                        />

                        {/* Choices section for LIST type */}
                        {formData.type === AttributeValueType.LIST && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Choices * (at least one required)
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        label="Add choice"
                                        variant="outlined"
                                        size="small"
                                        value={newChoiceInput}
                                        onChange={(e) => setNewChoiceInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={addChoice}
                                        disabled={!newChoiceInput.trim()}
                                    >
                                        Add
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {formData.choices.map((choice, index) => (
                                        <Chip
                                            key={`choice-${index}-${choice}`}
                                            label={choice}
                                            onDelete={() => removeChoice(choice)}
                                            deleteIcon={<DeleteIcon />}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsModalOpen(false);
                            resetForm();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateAttributeClick}
                        variant="contained"
                        disabled={
                            !formData.name ||
                            !formData.type ||
                            !formData.category ||
                            (formData.type === AttributeValueType.LIST &&
                                formData.choices.length === 0)
                        }
                    >
                        Create Attribute
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Attribute Creation</DialogTitle>
                <DialogContent>
                    <Typography>
                        Attributes cannot be removed after they are created. Are you sure you want
                        to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateAttribute} variant="contained" color="primary">
                        Yes, Create Attribute
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AttributesPage;
